import {
  InstagramPostsPaging,
  InstagramPost,
  DMAutomationTriggerContentType,
  DMAutomationTrigger,
} from "@markit/common.types";
import { API, API_BASE } from "../API";
import { collectionGroup, db, getDocs, query, where } from "../firebase";

export const INSTAGRAM_APP_ID = "1684761156047354";

// Builds the Instagram OAuth URL for the given user ID
export const getInstagramOAuthURL = (userId: string) => {
  const params = new URLSearchParams({
    client_id: INSTAGRAM_APP_ID,
    redirect_uri: `${API_BASE}instagramApi/redirect`,
    response_type: "code",
    scope: [
      "instagram_business_basic",
      "instagram_business_manage_messages",
      "instagram_business_manage_comments",
      // "instagram_business_manage_insights",
    ].join(","),
    state: userId,
  });

  return `https://www.instagram.com/oauth/authorize?${params.toString()}`;
};

/**
 * Authenticated GET to `/instagramApi/getUserPostsReels` (Firebase Bearer token).
 * Paginate with `after` from the previous response's `paging.nextCursor`.
 * Throws on failure (including a stale/invalidated connection) so callers can tell
 * a fetch error apart from a genuinely empty account - do not swallow here.
 */
export const fetchInstagramPostsPage = async (
  igAccountId: string,
  windowSize: number,
  after?: string,
  contentId?: string
): Promise<{ posts: InstagramPost[]; paging: InstagramPostsPaging }> => {
  const response = await API.instagramApi.getUserPostsReels({
    igAccountId,
    windowSize,
    afterCursor: after ?? null,
    specificPostId: contentId ?? null,
  });
  if (!response) {
    throw new Error("Failed to fetch Instagram posts");
  }
  return response;
};

/**
 * Authenticated GET to `/instagramApi/getUserStories` - the account's currently-live
 * stories, in the same shape as `fetchInstagramPostsPage` (never has more pages).
 */
export const fetchInstagramStoriesPage = async (
  igAccountId: string
): Promise<{ posts: InstagramPost[]; paging: InstagramPostsPaging }> => {
  const response = await API.instagramApi.getUserStories({ igAccountId });
  if (!response) {
    throw new Error("Failed to fetch Instagram stories");
  }
  return response;
};

// Disconnects a single connected Instagram account
export const disconnectInstagramAccount = (igAccountId: string) =>
  API.instagramApi.handleInstagramUserDisconnect({ igAccountId });

// Re-syncs username/profile pic and webhook subscription for an account
export const refreshInstagramAccount = async (igAccountId: string) => {
  const response = await API.instagramApi.handleInstagramRefreshAccount({
    igAccountId,
  });
  return response;
};

// Returns the igAccountId of every account that already has an active
// Next Post trigger (excluding excludeTriggerId, so editing/re-saving an
// existing Next Post trigger doesn't flag itself).
// Pass DMAutomationTriggerContentType.NEXT_STORY as contentId for the Next Story variant.
export const getInstagramNextTriggerAccountIds = async (
  userId: string,
  excludeTriggerId?: string,
  contentId: string = DMAutomationTriggerContentType.NEXT
): Promise<string[]> => {
  try {
    const query_ = query(
      collectionGroup(db, "dmAutomationTriggers"),
      where("createdBy", "==", userId),
      where("contentId", "==", contentId)
    );
    const snapshot = await getDocs(query_);
    return snapshot.docs
      .map((doc) => doc.data() as DMAutomationTrigger)
      .filter((trigger) => trigger.id !== excludeTriggerId)
      .map((trigger) => trigger.igAccountId)
      .filter((igAccountId): igAccountId is string => !!igAccountId);
  } catch (e) {
    console.error(e);
    return [];
  }
};
